import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { TextInput } from "src/qubCommon/Inputs";

const initialSupplierValues = {
  id: null,
  afm: "",
  onomasia: "",
  commerTitle: "",
  doy: "",
  doyDescr: "",
  postalAddress: "",
  postalAddressNo: "",
  postalAreaDescription: "",
  postalZipCode: "",
};

const supplierSchema = yup.object({
  afm: yup
    .string()
    .trim()
    .required("Απαιτείται ΑΦΜ.")
    .matches(/^\d{9}$/, "Το ΑΦΜ πρέπει να έχει 9 ψηφία."),
  onomasia: yup.string().trim().required("Απαιτείται επωνυμία."),
  commerTitle: yup.string().nullable(),
  doy: yup.string().nullable(),
  doyDescr: yup.string().nullable(),
  postalAddress: yup.string().nullable(),
  postalAddressNo: yup.string().nullable(),
  postalAreaDescription: yup.string().nullable(),
  postalZipCode: yup.string().nullable(),
});

const getSupplierValues = (supplier, initialAfm, initialSupplierName) => {
  const createName = supplier?.id ? "" : initialSupplierName ?? "";

  return {
    ...initialSupplierValues,
    ...supplier,
    afm: supplier?.afm ?? initialAfm ?? "",
    onomasia: supplier?.onomasia ?? createName,
    commerTitle: supplier?.commerTitle ?? createName,
    doy: supplier?.doy ?? "",
    doyDescr: supplier?.doyDescr ?? "",
    postalAddress: supplier?.postalAddress ?? "",
    postalAddressNo: supplier?.postalAddressNo ?? "",
    postalAreaDescription: supplier?.postalAreaDescription ?? "",
    postalZipCode: supplier?.postalZipCode ?? "",
  };
};

const normalizeOptionalString = (value) => value?.trim?.() || null;

const normalizeAfm = (value) => value?.replace?.(/\D/g, "").slice(0, 9) ?? "";

const inputSx = {
  "& .MuiInputBase-input": {
  },
};

const SupplierDialog = ({
  open,
  supplier,
  initialAfm,
  initialSupplierName = "",
  isSaving = false,
  onCancel,
  onSave,
}) => {
  const isEditMode = !!supplier?.id;
  const form = useForm({
    resolver: yupResolver(supplierSchema),
    defaultValues: initialSupplierValues,
  });
  const {
    handleSubmit,
    reset,
    setValue,
  } = form;

  useEffect(() => {
    if (!open) return;

    reset(getSupplierValues(supplier, initialAfm, initialSupplierName));
  }, [initialAfm, initialSupplierName, open, reset, supplier]);

  const handleSave = (values) => {
    onSave?.({
      id: values.id,
      afm: values.afm?.trim(),
      onomasia: values.onomasia?.trim(),
      commerTitle: normalizeOptionalString(values.commerTitle),
      doy: normalizeOptionalString(values.doy),
      doyDescr: normalizeOptionalString(values.doyDescr),
      postalAddress: normalizeOptionalString(values.postalAddress),
      postalAddressNo: normalizeOptionalString(values.postalAddressNo),
      postalAreaDescription: normalizeOptionalString(
        values.postalAreaDescription
      ),
      postalZipCode: normalizeOptionalString(values.postalZipCode),
    });
  };

  return (
    <FormProvider {...form}>
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
        <DialogTitle variant='h4' sx={{ fontWeight: 900 }}>
          {isEditMode ? "Επεξεργασία Προμηθευτή" : "Νέος Προμηθευτής"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextInput
                name="afm"
                label="ΑΦΜ"
                rules={{
                  setValueAs: normalizeAfm,
                }}
                blurFn={(event) =>
                  setValue("afm", normalizeAfm(event.target.value), {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextInput
                name="onomasia"
                label="Επωνυμία"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                name="commerTitle"
                label="Διακριτικός Τίτλος"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextInput
                name="doy"
                label="ΔΟΥ"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextInput
                name="doyDescr"
                label="Περιγραφή ΔΟΥ"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextInput
                name="postalAddress"
                label="Οδός"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextInput
                name="postalAddressNo"
                label="Αριθμός"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextInput
                name="postalZipCode"
                label="ΤΚ"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextInput
                name="postalAreaDescription"
                label="Πόλη"
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit(handleSave)}
            disabled={isSaving}
          >
            Αποθήκευση
          </Button>
          <Button variant="contained" color="error" onClick={onCancel}>
            Άκυρο
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default SupplierDialog;
